/**
 * TypeScript mirror of the worker's station_clearance.json (schema v1) and of
 * the summary block postprocess writes onto the derived layer's metadata.
 * Source of truth: scripts/clearance.py :: compute_clearance_bins (the
 * `result` dict) and write_station_clearance (the summary sidecar). Keep every
 * key in sync with those two functions.
 */
export const CLEARANCE_SCHEMA_VERSION = 1;

/** ASPRS classification written on violating points — scripts/clearance.py. */
export const CLEARANCE_VIOLATION_CLASS = 13;

/** `[offsetM, heightM]`. Offset is signed left(−) / right(+) of the centreline. */
export type ClearanceVertex = [number, number];

export interface ClearanceProfileSnapshot {
  name: string;
  /**
   * The published standard the dimensions came from, snapshotted with them
   * (ruling K4): "Dimensions per Transport Canada E-05, Diagram 1 — verify
   * against the current standard". `null` for a hand-drawn profile.
   */
  sourceAttribution: string | null;
  /** First vertex is NOT repeated — the polygon is implicitly closed. */
  vertices: ClearanceVertex[];
}

export interface ClearanceAlignmentMeta {
  name: string;
  key: string;
  layerId: string | null;
  /** Resolved server-side by preprocess; absent when the layer is gone. */
  layerName?: string;
  sourceHash: string;
  lengthM: number;
  staStart: number;
  equations: { staInternal: number; staAhead: number }[];
}

export interface ClearanceRunParams {
  stationIntervalM: number;
  verticalOffsetM: number;
  stationFromInternalM: number;
  stationToInternalM: number;
  profileMinOffsetM: number;
  profileMaxOffsetM: number;
  profileMinHeightM: number;
  profileMaxHeightM: number;
  violationClass: number;
  /**
   * Half-span of the viewer's SYMMETRIC display range (rulings R8/P9):
   * **P98 of the intrusion depths** (`d > 0`), floored at
   * `RAMP_SPAN_FLOOR_M = max(nearMissBandsM) = 0.35 m`. Computed in exactly
   * one place — `ramp_span_m` in `scripts/clearance.py` — and passed through
   * by `postprocess.ts`, which writes
   * `clearanceRange = [-rampSpanM, +rampSpanM]`. That symmetry is what puts
   * the profile boundary at the ramp's exact midpoint, which is what lets the
   * fixed `CLEARANCE` gradient park its amber break on the decision line.
   * Nothing is baked into the LAZ's RGB.
   *
   * Two statistics were rejected on real data:
   * - `max |d|` (finding I1) let the deepest OUTSIDE point — routinely
   *   background terrain metres below the datum — squeeze every violation and
   *   near-miss into a sliver at the ramp's centre.
   * - `max d` over the intrusions (ruling P9) is still an outlier statistic:
   *   one tree limb three metres inside the envelope set the span for a whole
   *   run, so the analysis found the violations and the picture hid them.
   *
   * The floor is the WIDEST near-miss band rather than a value between bands,
   * so the ladder the run reports is always inside the range it is drawn over
   * — proving a clean run IS clean needs visible margins.
   *
   * Consequence: `ClearanceSummary.maxIntrusionM` can EXCEED `rampSpanM`. The
   * worst 2 % of intrusions saturate at the gradient's darkest red instead of
   * getting their own shade; the dock still quotes the exact worst value.
   */
  rampSpanM: number | null;
  /** The fixed near-miss bands this run used, in metres. `[0.1, 0.2, 0.35]` in v1. */
  nearMissBandsM: number[];
  method: 'NEAREST_STATION_FRAME';
}

/** Cumulative, nested counts of points OUTSIDE the profile within each band. */
export interface ClearanceNearMiss {
  within0_1M: number;
  within0_2M: number;
  within0_35M: number;
}

export interface ClearanceSummary {
  binCount: number;
  /** Every point in the cloud, scored or not — the denominator for `scoredPointCount`. */
  totalPointCount: number;
  scoredPointCount: number;
  violationCount: number;
  violationPct: number;
  /** null when nothing intruded. */
  maxIntrusionM: number | null;
  worstBinIndex: number | null;
  worstStationInternalM: number | null;
  worstStationDisplayM: number | null;
  worstOffsetM: number | null;
  worstHeightM: number | null;
  /**
   * The tightest clearance margin as a POSITIVE distance (a margin is a
   * distance, not a signed coordinate) — `null` when every scored point
   * intruded. Same sign as `scripts/clearance.py`'s `min_margin`.
   */
  minMarginM: number | null;
  binsWithViolations: number;
  nearMiss: ClearanceNearMiss;
}

export interface ClearanceBin {
  i: number;
  staStartInternalM: number;
  staEndInternalM: number;
  staStartDisplayM: number;
  staEndDisplayM: number;
  pointCount: number;
  violationCount: number;
  /** null in a bin with no violation — an em dash, never a zero. */
  maxIntrusionM: number | null;
  worstOffsetM: number | null;
  worstHeightM: number | null;
  /**
   * Ruling K2/R12: how many points sat OUTSIDE the profile within 0.1 / 0.2 /
   * 0.35 m of it — the ARTC contingency ladder. Nested, so
   * `within0_1M <= within0_2M <= within0_35M`, and a violation is never
   * counted here (it is already `violationCount`).
   */
  nearMiss: ClearanceNearMiss;
}

export interface ClearanceJson {
  schemaVersion: number;
  analysisKind: 'clearance';
  alignment: ClearanceAlignmentMeta;
  profile: ClearanceProfileSnapshot;
  params: ClearanceRunParams;
  summary: ClearanceSummary;
  bins: ClearanceBin[];
}

/**
 * What lands on `layers.metadata.clearance` (~1.5 KB). `byproducts.{json,csv,laz}`
 * are the registered byproduct FILENAMES, not S3 keys — match against
 * `BatchByproduct.filename`. The per-bin array is deliberately NOT here: this
 * block rides on the `layers` query for every layer at project load.
 */
export interface ClearanceLayerMeta {
  schemaVersion: number;
  alignment: ClearanceAlignmentMeta;
  profile: ClearanceProfileSnapshot;
  params: ClearanceRunParams;
  summary: ClearanceSummary;
  byproducts: { json: string | null; csv: string | null; laz: string | null; section?: string | null };
  /**
   * Descriptor for the per-station cross-section grid
   * (`<stem>_station_clearance_section.u16`). OPTIONAL on purpose: a run from
   * a worker older than the section feature has none, and the Profile tab
   * falls back to the static sketch rather than erroring.
   */
  sectionBins?: ClearanceSectionBins | null;
  pointcloudLayerId: string;
  pointcloudLayerName: string;
}

/** The slice of a clearance-derived layer's metadata the client reads. Typed
 *  rather than `as any` — `Layer.metadata` is an untyped JSON scalar. */
export interface DerivedClearanceLayerMetadata {
  batchId?: string;
  derivedFrom?: string;
  sourceProcessJobId?: string;
  clearance?: ClearanceLayerMeta | null;
  clearanceRange?: [number, number];
}

/**
 * The `sectionBins` block `clearance.py` writes into `clearance_summary.json`,
 * carried onto the layer metadata by the worker.
 *
 * The client trusts THIS for the grid's shape and extents — never its own
 * arithmetic from the profile bounds. The writer chooses the cell size
 * adaptively (it doubles until the grid fits a cell budget), so a reader that
 * assumed 0.05 m would silently draw a long run's section at the wrong scale.
 * Same failure mode as the station-volumes `offsetBins` block, which CLAUDE.md
 * documents: one writer, two readers, nothing type-checked across the
 * Python/TS boundary.
 */
export interface ClearanceSectionBins {
  file: string;
  planes: string[];
  nStations: number;
  nOffsets: number;
  nHeights: number;
  offsetStartM: number;
  offsetStepM: number;
  heightStartM: number;
  heightStepM: number;
  saturatedAt: number;
}
